import { FormikProps, useFormik } from 'formik'
import { format } from 'date-fns'
import {
  ButtonV2,
  DatePickerV2,
  LabraToolTipV2,
  labraUIDateFormat,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
  VerticalDivider,
  useAuth,
} from '@labrav/react-components'
import { FormField } from '@labrav/react-components/lib/@types/types/forms'
import { DateTime } from 'luxon'
import React, { useCallback, useMemo, useState } from 'react'

import {
  expirationDateValidationErrorMsg,
  isLoading,
  LoadingTypes,
  usageEndDateError,
  useCurrentCloudProvider,
} from '@labrav/common'
import { faCalendarPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import * as yup from 'yup'

import { DetailInfo } from '../../common/components/DetailRow/DetailRow'
import { updateChannelPartnerPrivateOffer } from '../../modules/channelPartnerPrivateOffer/action'
import {
  OfferStatus,
  OfferTypeRenew,
  Subscription,
} from '../../modules/privateOffer/types'
import { useDispatch, useSelector } from '../../store'
import { useStyles } from './ExtendOfferExpirationDateModal.styles'

export type ExtendOfferExpirationDateProps = {
  offerExpirationDate: string
  offerId: string
  offerSubscriptionEndDate?: string
  planType?: PlanType
  offerStatus?: OfferStatus
  isDraft?: boolean
  agreementStartDate?: string | null
  type?: OfferTypeRenew
  labraSubscriptionId?: string | null // for ABO
  subscription?: Subscription // for ABO
  openExtendExpiryDateModal?: boolean
  onAcceptModalAction?: (data: Record<string, unknown>) => void
  onDeclineModalAction?: () => void
  extendOfferType?: string
  resaleAuthorizationId?: string
  partnerIdFromURL?: string
  limitForExpirationDate?: boolean
}

export const ExtendOfferExpirationDateModal = React.memo(
  ({
    offerExpirationDate,
    offerId,
    planType,
    offerSubscriptionEndDate,
    offerStatus,
    agreementStartDate,
    type,
    labraSubscriptionId,
    subscription,
    openExtendExpiryDateModal,
    onAcceptModalAction,
    onDeclineModalAction,
    extendOfferType,
    resaleAuthorizationId,
    partnerIdFromURL,
    limitForExpirationDate = true,
  }: ExtendOfferExpirationDateProps) => {
    const { classes } = useStyles()
    const selectedCloudType = useCurrentCloudProvider()
    // const expirationDatePlusFiveDaysString = format(
    //   addDays(new Date(offerExpirationDate), 5),
    //   'yyyy-MM-dd'
    // )
    const today = new Date()
    const todayFormatted = format(today, 'yyyy-MM-dd')

    const subscriptionEndDate = offerSubscriptionEndDate
      ? DateTime.fromISO(offerSubscriptionEndDate)
      : ''
    const subscriptionEndDateString = subscriptionEndDate
      ? subscriptionEndDate.toISODate()
      : ''
    const formattedAgreementStartDate = agreementStartDate
      ? DateTime.fromISO(agreementStartDate).toFormat('dd-MM-yyyy')
      : undefined

    const loading = useSelector(state =>
      isLoading(state.loading)(LoadingTypes.UPDATE_CHANNEL_OFFER)
    )
    const [openExpiryDateModal, setExpiryDateModal] = useState<boolean>(false)
    const [isCustomExpirationDate, setIsCustomExpirationDate] =
      useState<boolean>(true)
    const handleClose = useCallback(() => {
      formik.resetForm()
      setExpiryDateModal(false)
      onDeclineModalAction && onDeclineModalAction()
    }, [openExpiryDateModal])

    const isAWSRenewalOffer =
      type === OfferTypeRenew.RENEWAL && !isEmpty(labraSubscriptionId)

    const fields: FormField[] = [
      {
        title: 'New offer expiration date',
        humanReadableKey: 'New offer expiration date',
        placeHolder: 'New offer expiration date',
        id: 'offerExpirationDate',
        isRequired: true,
        isReadOnly: !isCustomExpirationDate,
        isCustom: false,
        defaultValue: 'today',
        dataType: 'date',
        minDate: 'today',
      },
    ]

    // const extendByDaysRadioButtonMapping = [
    //   {
    //     key: '5',
    //     label: 'By 5 days',
    //     type: RadioButtonType.STANDARD,
    //   },
    //   {
    //     key: '10',
    //     label: 'By 10 days',
    //     type: RadioButtonType.STANDARD,
    //   },
    //   {
    //     key: '15',
    //     label: 'By 15 days',
    //     type: RadioButtonType.STANDARD,
    //   },
    //   {
    //     key: 'custom',
    //     label: 'Custom',
    //     type: RadioButtonType.STANDARD,
    //   },
    // ]
    const initialValue: Record<string, unknown> = {
      offerExpirationDate: todayFormatted,
      subscriptionEndDate: subscriptionEndDateString,
    }

    const validateSchema = yup.object().shape({
      offerExpirationDate: yup
        .string()
        .required(expirationDateValidationErrorMsg)
        .typeError(expirationDateValidationErrorMsg)
        .test('update-expiration-date', function (value) {
          if (agreementStartDate && limitForExpirationDate) {
            const agreementStart = DateTime.fromISO(agreementStartDate)
            const expirationDate = DateTime.fromISO(value || '')
            if (!(expirationDate < agreementStart)) {
              return this.createError({
                message: `Offer Expiration Date should be smaller than (${formattedAgreementStartDate}).`,
              })
            }
          }

          if (isAWSRenewalOffer) {
            const expirationDate = DateTime.fromISO(value || '')
            const expectedExpirationDate = DateTime.fromISO(
              subscription?.expectedExpirationDate || ''
            )
            const offerSubscriptionEndDate = DateTime.fromISO(
              this.parent.subscriptionEndDate || ''
            )
            const comparisonDate =
              expectedExpirationDate < offerSubscriptionEndDate
                ? expectedExpirationDate
                : offerSubscriptionEndDate

            const formattedComparisonDate =
              comparisonDate.toFormat('dd-MM-yyyy')
            const message = comparisonDate.equals(expectedExpirationDate)
              ? `Offer Expiration Date should be smaller than or equal to the previous service end date (${formattedComparisonDate}).`
              : `Offer Expiration Date should be smaller than or equal to the service end date (${formattedComparisonDate}).`
            if (!(expirationDate <= comparisonDate)) {
              return this.createError({
                message: message,
              })
            }
          }
          return true
        }),
      subscriptionEndDate: yup
        .string()
        .nullable()
        .test(
          'check offer subscription end date',
          usageEndDateError,
          function (value?: string | null) {
            if (!isEmpty(value)) {
              if (isAWSRenewalOffer || agreementStartDate) {
                return true
              }
              return (
                DateTime.fromISO(this.parent.offerExpirationDate) <
                DateTime.fromISO(value || '')
              )
            }
            return true
          }
        ),
    })

    const formik = useFormik({
      initialValues: initialValue,
      validationSchema: validateSchema,
      validateOnBlur: true,
      validateOnChange: true,
      validateOnMount: true,
      // enableReinitialize: true,
      onSubmit: () => void 0,
    })

    const finalFields: FormField[] = useMemo(() => {
      if (
        planType === PlanType.SAAS_SUBSCRIPTION &&
        selectedCloudType === 'AWS'
      ) {
        fields.push({
          title: 'Usage end date',
          humanReadableKey: 'Usage end date',
          placeHolder: 'Usage end date',
          id: 'subscriptionEndDate',
          isRequired: true,
          isReadOnly: false,
          isCustom: false,
          defaultValue: subscriptionEndDateString || undefined,
          dataType: 'date',
          minDate: DateTime.fromISO(
            formik.values.offerExpirationDate as string,
            { zone: 'UTC' }
          )
            .plus({ day: 1 })
            .toString(),
        })
      }
      return [...fields]
    }, [planType, formik.values.offerExpirationDate])

    const disabled = useMemo(() => {
      return (
        !isEmpty(formik.errors.offerExpirationDate) ||
        !isEmpty(formik.errors?.subscriptionEndDate || '')
      )
    }, [formik.errors.offerExpirationDate, formik.errors?.subscriptionEndDate])

    const dispatch = useDispatch()

    const handleAccept = useCallback(async () => {
      await formik.submitForm()
      setExpiryDateModal(false)
      const dataToBeSent: Record<string, unknown> = {
        offerExpirationDate:
          selectedCloudType !== 'GCP'
            ? DateTime.fromISO(
                formik.values.offerExpirationDate as string
              ).toISODate()
            : DateTime.fromISO(
                formik.values.offerExpirationDate as string
              ).toISO(),
      }
      if (
        planType === PlanType.SAAS_SUBSCRIPTION &&
        selectedCloudType === 'AWS'
      ) {
        dataToBeSent['subscriptionEndDate'] = DateTime.fromISO(
          formik.values.subscriptionEndDate as string
        ).toISODate()
      }
      if (selectedCloudType === 'GCP') {
        dataToBeSent['offerStatus'] = offerStatus
      }
      if (onAcceptModalAction && extendOfferType == 'CPPO') {
        onAcceptModalAction(dataToBeSent)
      } else {
        // implement after Private offer integration
        await dispatch(
          updateChannelPartnerPrivateOffer(
            offerId,
            { ...dataToBeSent },
            undefined,
            false,
            () => void 0,
            resaleAuthorizationId,
            partnerIdFromURL,
            selectedCloudType
          )
        )
      }
    }, [
      formik.values.offerExpirationDate,
      formik.values.subscriptionEndDate,
      planType,
      onAcceptModalAction,
    ])

    // const setExpirationDate = (daysToAdd: number) => {
    //   if (offerExpirationDate) {
    //     const newExpirationDate = format(
    //       addDays(new Date(offerExpirationDate), daysToAdd),
    //       'yyyy-MM-dd'
    //     )
    //     formik.setFieldValue('offerExpirationDate', newExpirationDate)
    //     setIsCustomExpirationDate(false)
    //   }
    // }

    // const handleSelection = (event: ChangeEvent<HTMLInputElement>): number => {
    //   const selectedDuration = event.target.value as string
    //   switch (selectedDuration) {
    //     case '5':
    //     case '10':
    //     case '15':
    //       setExpirationDate(parseInt(selectedDuration, 10))
    //       return parseInt(selectedDuration, 10)
    //     case 'custom':
    //       setIsCustomExpirationDate(true)
    //       formik.setFieldValue('offerExpirationDate', '')
    //       return 0
    //     default:
    //       return 0
    //   }
    // }

    // const checkedValue = useMemo(() => {
    //   const expDate = DateTime.fromISO(offerExpirationDate)
    //   const diffInDays = DateTime.fromISO(
    //     formik.values.offerExpirationDate as string
    //   )
    //     .endOf('day')
    //     .diff(expDate, 'days')
    //     .toObject()?.days
    //   switch (Math.floor(diffInDays as number) || 'custom') {
    //     case 5:
    //       return '5'
    //     case 10:
    //       return '10'
    //     case 15:
    //       return '15'
    //     default:
    //       return 'custom'
    //   }
    // }, [formik.values.offerExpirationDate, offerExpirationDate])
    return (
      <div data-testid="extend-offer-expiry-modal">
        <ModalConfirmV2
          title={
            <>
              {' '}
              <FontAwesomeIcon
                icon={faCalendarPlus}
                style={{
                  width: '16px',
                  height: '16px',
                  fontSize: '16px',
                  padding: '0px 12px 0px 0px',
                  color: '#212121',
                }}
              />
              Extend expiration date
            </>
          }
          content={
            <>
              <DetailInfo
                title={'Current offer expiration date'}
                value={
                  <div className={classes.customWrapper}>
                    {format(new Date(offerExpirationDate), labraUIDateFormat)}
                  </div>
                }
                keyName={'offerExpirationDate'}
              />{' '}
              {/* <br></br> */}
              {/* <div className={classes.helpText}>
                Extend expiration date by selecting one of the options below or
                by adding a custom date .
              </div>
              <br></br>
              <div className={classes.radioButton}>
                <RadioButtonsV2
                  checkedValue={{
                    key: checkedValue,
                    value: checkedValue,
                  }}
                  name="offerExpirationDate"
                  radioButtonMappings={extendByDaysRadioButtonMapping}
                  handleChange={handleSelection}
                />
              </div> */}
              <ShowDateInput formik={formik} fields={finalFields} />
            </>
          }
          open={!!openExtendExpiryDateModal}
          titleAcceptButton={'Extend expiration date'}
          titleCancelButton={'Cancel'}
          onAccept={handleAccept}
          onDecline={handleClose}
          variant={ModalSizeVariants.medium}
          hideCancelButton={false}
          acceptDisabled={loading}
        />
      </div>
    )
  }
)
export type ShowDateInputProps = {
  fields: FormField[]
  formik: FormikProps<Record<string, unknown>>
}
export const ShowDateInput = React.memo(
  ({ fields, formik }: ShowDateInputProps) => {
    const { classes } = useStyles()
    return (
      <>
        {fields.map(field => (
          <div className={classes.dateField} key={`div-${field.id}`}>
            <DatePickerV2
              formik={formik}
              format={labraUIDateFormat}
              field={field}
              key={field.id}
              views={['year', 'month', 'day']}
            />
          </div>
        ))}
      </>
    )
  }
)

interface ExtendOfferExpirationProps {
  offerExpirationDate: string
  offerId: string
  offerSubscriptionEndDate?: string
  planType?: PlanType
  offerStatus?: OfferStatus
  isDraft?: boolean
  agreementStartDate?: string | null
  type?: OfferTypeRenew
  extendOfferType?: string
  labraSubscriptionId?: string | null // for ABO
  subscription?: Subscription // for ABO
  updateOfferStatusCheck: boolean
  updateOfferExpiringToolTipMessage?: string
  classes: {
    updateButtonContainer: string
    disabledEdit: string
    extendOfferExpiry: string
  }
  resaleAuthorizationId?: string
  partnerIdFromURL?: string
  successMessageText?: string
  limitForExpirationDate?: boolean
}

export const ExtendOfferExpirationDateButton: React.FC<
  ExtendOfferExpirationProps
> = ({
  offerExpirationDate,
  offerId,
  planType,
  offerSubscriptionEndDate,
  offerStatus,
  isDraft,
  agreementStartDate,
  type,
  extendOfferType,
  labraSubscriptionId,
  subscription,
  updateOfferStatusCheck,
  updateOfferExpiringToolTipMessage,
  classes,
  resaleAuthorizationId,
  partnerIdFromURL,
  successMessageText,
  limitForExpirationDate = true,
}) => {
  const dispatch = useDispatch()
  const [openExpiryDateModalCPPO, setExpiryDateModalCPPO] = useState<string>('')
  const cloudMarketplace = useCurrentCloudProvider()

  const { loading: authLoading, canDo } = useAuth()

  const canWriteMarketplace = useMemo(
    () =>
      !authLoading &&
      canDo({
        cloud: cloudMarketplace.toLowerCase(),
        service: 'marketplace',
        action: 'create',
      }),
    [authLoading, canDo, cloudMarketplace]
  )

  const onAcceptModalAction = useCallback(
    async (data: Record<string, unknown>) => {
      await dispatch(
        updateChannelPartnerPrivateOffer(
          offerId,
          data,
          undefined,
          false,
          () => void 0,
          resaleAuthorizationId,
          partnerIdFromURL,
          cloudMarketplace,
          successMessageText
        )
      )
      setExpiryDateModalCPPO('')
    },
    [dispatch, offerId]
  )

  const onDeclineModalAction = useCallback(() => {
    setExpiryDateModalCPPO('')
  }, [])

  const handleOpenModal = useCallback(() => {
    if (!canWriteMarketplace) return
    setExpiryDateModalCPPO(offerId)
  }, [offerId, canWriteMarketplace])

  return (
    <>
      <VerticalDivider height="24px" />
      <div className={classes.updateButtonContainer}>
        {updateOfferStatusCheck ? (
          <LabraToolTipV2
            tooltipPlacement="top"
            description={updateOfferExpiringToolTipMessage}
          >
            <span>
              <ButtonV2
                className={classes.disabledEdit}
                styleType="link"
                data-testid={'extend-expiration-date-disabled'}
                disabled={true}
              >
                <FontAwesomeIcon
                  icon={faCalendarPlus}
                  style={{
                    width: '16px',
                    height: '16px',
                    fontSize: '16px',
                    padding: '0px 12px 0px 0px',
                    color: '#1942C5',
                  }}
                />
                Extend expiration date
              </ButtonV2>
            </span>
          </LabraToolTipV2>
        ) : (
          <LabraToolTipV2
            tooltipPlacement="top"
            description={
              !canWriteMarketplace ? (
                <Typography component="div">
                  This action is disabled because you have read-only access
                </Typography>
              ) : undefined
            }
          >
            <span>
              <ButtonV2
                className={classes.extendOfferExpiry}
                styleType="link"
                data-testid={'extend-expiration-date'}
                onClick={handleOpenModal}
                disabled={!canWriteMarketplace}
              >
                <FontAwesomeIcon
                  icon={faCalendarPlus}
                  style={{
                    width: '16px',
                    height: '16px',
                    fontSize: '16px',
                    padding: '0px 12px 0px 0px',
                    color: '#1942C5',
                  }}
                />
                Extend expiration date
              </ButtonV2>
            </span>
          </LabraToolTipV2>
        )}
      </div>
      <ExtendOfferExpirationDateModal
        offerExpirationDate={offerExpirationDate}
        offerId={offerId}
        planType={planType as PlanType}
        offerSubscriptionEndDate={offerSubscriptionEndDate}
        offerStatus={
          (offerStatus as OfferStatus) ||
          OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS
        }
        isDraft={isDraft}
        agreementStartDate={agreementStartDate}
        type={type as OfferTypeRenew}
        extendOfferType={extendOfferType}
        openExtendExpiryDateModal={!!openExpiryDateModalCPPO}
        onAcceptModalAction={onAcceptModalAction}
        onDeclineModalAction={onDeclineModalAction}
        labraSubscriptionId={labraSubscriptionId}
        subscription={subscription}
        resaleAuthorizationId={resaleAuthorizationId}
        partnerIdFromURL={partnerIdFromURL}
        limitForExpirationDate={limitForExpirationDate}
      />
    </>
  )
}
